import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Box, Switch } from '@mui/material';
import StyChip from 'routes/HumanResourcesModule/components/ui/StyChip';
import { formatDate } from 'helpers/dateHelper';
import Flex from 'ui/Flex/Flex';
import { getAgentDepartureHypothesisDisplayType } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/gridHelpers';
import { getPositionValue } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/gridHelpers';
import { AgentDepartureHypothesisTypesColor } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Departure/helpers/constants';
import { DetailedAgentDepartureHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/Departure/helpers/types';
import { orgChartHrFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { humanizedRecurrentValues } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { firstNameLastNameFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import { toggleExclusion } from '../helpers/dataHelpers';

type Props = {
    hypothesis: DetailedAgentDepartureHypothesis;
    excludedAgentDepartureHypotheses?: number[];
    setExcludedAgentDepartureHypotheses?: (
        excludedAgentDepartureHypotheses: number[],
    ) => void;
};

const AgentDepartureHypothesis = ({
    hypothesis,
    excludedAgentDepartureHypotheses,
    setExcludedAgentDepartureHypotheses,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const {
        id,
        recurrence,
        agentFirstName,
        agentLastName,
        agentDepartureHypothesisType,
        jobLabel,
        mantyStatus,
        orgChartHrName,
        orgChartHrMapping,
        startDate,
    } = hypothesis;
    const displayType = getAgentDepartureHypothesisDisplayType(
        recurrence,
        agentDepartureHypothesisType,
    );

    const displayMantyStatus = getPositionValue(mantyStatus);
    const displayRecurrence = humanizedRecurrentValues[recurrence];
    const isExcluded = excludedAgentDepartureHypotheses
        ? excludedAgentDepartureHypotheses.includes(id)
        : false;

    const chipColor = AgentDepartureHypothesisTypesColor[displayType];

    return (
        <Flex align="center" justify="space-between">
            <Flex direction="column" align="flex-start">
                <Typography variant="body1SemiBold">
                    {firstNameLastNameFormatter({
                        lastName: agentLastName,
                        firstName: agentFirstName,
                    })}
                </Typography>
                <Flex
                    flexDirection="column"
                    gap="1em"
                    alignItems="left"
                    paddingTop="1em"
                >
                    {jobLabel && (
                        <Typography
                            variant="body2"
                            sx={{ fontStyle: 'italic' }}
                        >
                            {jobLabel}
                        </Typography>
                    )}

                    {(orgChartHrName || orgChartHrMapping) && (
                        <Typography
                            variant="body2"
                            sx={{ fontStyle: 'italic' }}
                        >
                            {orgChartHrFormatter({
                                orgChartHrName,
                                orgChartHrMapping,
                            })}
                        </Typography>
                    )}

                    {displayMantyStatus && (
                        <Typography
                            variant="body2"
                            sx={{ fontStyle: 'italic' }}
                        >
                            {displayMantyStatus}
                        </Typography>
                    )}

                    {displayRecurrence && (
                        <Typography
                            variant="body2"
                            sx={{ fontStyle: 'italic' }}
                        >
                            {displayRecurrence}
                        </Typography>
                    )}
                </Flex>
            </Flex>

            <Box display="flex" alignItems="center">
                <StyChip
                    label={t(
                        `humanResources.simulation.agentDepartureHypothesisType.${displayType}`,
                    )}
                    styledColor={chipColor}
                    sx={{ marginRight: '8px' }}
                />
                <Typography variant="body2">
                    {formatDate(new Date(startDate))}
                </Typography>
                {excludedAgentDepartureHypotheses &&
                    setExcludedAgentDepartureHypotheses && (
                        <Switch
                            checked={!isExcluded}
                            onChange={() =>
                                toggleExclusion(
                                    excludedAgentDepartureHypotheses,
                                    setExcludedAgentDepartureHypotheses,
                                    id,
                                    isExcluded,
                                )
                            }
                        />
                    )}
            </Box>
        </Flex>
    );
};

export default AgentDepartureHypothesis;
